const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const headers = {
  'Content-Type': 'application/json',
  'x-api-key': 'scopingai',
};

export type TouChannel = 'E' | 'B';

export type TouRateRow = {
  id: string;
  retailer_name: string;
  state: string;
  /** NEM12 channel (migration 116): E = general usage, B = solar feed-in. */
  channel: TouChannel;
  rate_type: string;
  time_range: string | null;
  weekdays: number[] | null;
  holiday_names: string[] | null;
  source_nmi: string | null;
  last_updated: string | null;
  notes: string | null;
  start_time: string | null;
  end_time: string | null;
  created_at: string;
  updated_at: string;
};

export type ListParams = { search?: string; retailer?: string; state?: string; channel?: TouChannel | ''; page?: number; limit?: number };
export type ListResponse<T> = { success: boolean; data?: T[]; count?: number; page?: number; limit?: number; error?: string };
export type MutateResponse<T> = { success: boolean; data?: T; error?: string };
export type BulkUploadResponse = {
  success: boolean;
  total?: number;
  inserted?: number;
  skipped?: number;
  /** Rows skipped because the same window already exists (included in `skipped`). */
  duplicates?: number;
  errors?: Array<{ row: number; error: string }>;
  error?: string;
};

export const touRatesAdminService = {
  list: async (params: ListParams = {}): Promise<ListResponse<TouRateRow>> => {
    try {
      const qs = new URLSearchParams();
      if (params.search) qs.set('search', params.search);
      if (params.retailer) qs.set('retailer', params.retailer);
      if (params.state) qs.set('state', params.state);
      if (params.channel) qs.set('channel', params.channel);
      qs.set('page', String(params.page ?? 1));
      qs.set('limit', String(params.limit ?? 25));
      const res = await fetch(`${API_BASE_URL}/api/tou-rates/list?${qs.toString()}`, { headers });
      return (await res.json()) as ListResponse<TouRateRow>;
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Request failed' };
    }
  },

  getRetailers: async (): Promise<string[]> => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/tou-rates/retailers`, { headers });
      const json = await res.json();
      return json.success ? (json.data as string[]) : [];
    } catch {
      return [];
    }
  },

  getHolidayNames: async (state?: string): Promise<string[]> => {
    try {
      const qs = state ? `?state=${encodeURIComponent(state)}` : '';
      const res = await fetch(`${API_BASE_URL}/api/tou-rates/holiday-names${qs}`, { headers });
      const json = await res.json();
      return json.success ? (json.data as string[]) : [];
    } catch {
      return [];
    }
  },

  create: async (body: Partial<TouRateRow>): Promise<MutateResponse<TouRateRow>> => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/tou-rates`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      return (await res.json()) as MutateResponse<TouRateRow>;
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Request failed' };
    }
  },

  update: async (id: string, body: Partial<TouRateRow>): Promise<MutateResponse<TouRateRow>> => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/tou-rates/${id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(body),
      });
      return (await res.json()) as MutateResponse<TouRateRow>;
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Request failed' };
    }
  },

  remove: async (id: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/tou-rates/${id}`, {
        method: 'DELETE',
        headers,
      });
      return (await res.json()) as { success: boolean; error?: string };
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Request failed' };
    }
  },

  bulkUpload: async (file: File): Promise<BulkUploadResponse> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_BASE_URL}/api/tou-rates/bulk`, {
        method: 'POST',
        headers: { 'x-api-key': 'scopingai' },
        body: formData,
      });
      return (await res.json()) as BulkUploadResponse;
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Upload failed' };
    }
  },
};
